import { Bank, CheckCircle, WarningCircle, CurrencyDollar, Flag, Clock } from "@phosphor-icons/react";

const SimpleRow = ({ label, value }) => (
  <div className="flex justify-between items-start py-2.5 border-b border-zinc-800/50 last:border-b-0">
    <span className="text-xs text-zinc-400">{label}</span>
    <span className="text-xs text-zinc-100 font-medium text-right max-w-[60%]">{value}</span>
  </div>
);

const PointList = ({ items, tone = "good" }) => (
  <ul className="space-y-2">
    {items.map((it, i) => (
      <li key={i} className="flex items-start gap-2 text-xs text-zinc-300 leading-relaxed">
        {tone === "good"
          ? <CheckCircle size={13} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" />
          : <WarningCircle size={13} weight="fill" className="text-red-400 shrink-0 mt-0.5" />}
        <span>{it}</span>
      </li>
    ))}
  </ul>
);

export default function RetireTSP() {
  return (
    <div className="p-5 max-w-[1400px] space-y-6" data-testid="retire-tsp-page">
      {/* Hero */}
      <div className="bg-zinc-900/50 border border-zinc-800/80 rounded-sm p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-sm bg-violet-900/20 flex items-center justify-center">
            <Flag size={20} weight="bold" className="text-violet-400" />
          </div>
          <div>
            <h2 className="text-lg font-heading font-bold text-zinc-50">TSP (Thrift Savings Plan)</h2>
            <p className="text-xs text-zinc-400">Retirement account for federal employees and US military</p>
          </div>
        </div>
        <p className="text-sm text-zinc-300 leading-relaxed">
          The Thrift Savings Plan is the government's version of a 401(k). It's for federal civilian workers and active-duty military. The rules are very similar to a 401(k), but TSP has rock-bottom fees and a much smaller, simpler menu of investment funds.
        </p>
      </div>

      {/* Quick facts */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-sm font-heading font-bold text-zinc-100 mb-3 flex items-center gap-2">
          <Clock size={14} weight="bold" className="text-violet-400" /> Quick Facts (2026)
        </h3>
        <SimpleRow label="Who can use it" value="Federal employees · Active military · Reserves" />
        <SimpleRow label="2026 contribution limit" value="$23,500 (under 50) · $31,000 (50+)" />
        <SimpleRow label="Government match (FERS / BRS)" value="Up to 5% of pay (first 1% free + 4% match)" />
        <SimpleRow label="Tax treatment (Traditional)" value="Pre-tax now, taxed on withdrawal" />
        <SimpleRow label="Tax treatment (Roth TSP)" value="After-tax now, tax-free on withdrawal" />
        <SimpleRow label="Average expense ratio" value="~0.05% (one of lowest in industry)" />
        <SimpleRow label="Withdraw without penalty" value="Age 59½ (or age 55 if you separate from federal service)" />
        <SimpleRow label="RMDs start" value="Age 73" />
      </div>

      {/* The 6 funds */}
      <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-sm p-5">
        <h3 className="text-sm font-heading font-bold text-zinc-100 mb-3 flex items-center gap-2">
          <CurrencyDollar size={14} weight="bold" className="text-emerald-400" /> The 5 Core Funds + Lifecycle (L) Funds
        </h3>
        <div className="space-y-2 text-xs text-zinc-300 leading-relaxed">
          <p><span className="font-bold text-zinc-50">G Fund</span> — Government securities. Zero risk, ~3-4% return. The "savings account" of TSP.</p>
          <p><span className="font-bold text-zinc-50">F Fund</span> — US bond index. Low risk, modest return.</p>
          <p><span className="font-bold text-zinc-50">C Fund</span> — S&amp;P 500 index. Tracks 500 largest US companies.</p>
          <p><span className="font-bold text-zinc-50">S Fund</span> — Small-cap US stock index. Smaller US companies, higher growth potential, more volatile.</p>
          <p><span className="font-bold text-zinc-50">I Fund</span> — International stock index. Non-US developed markets.</p>
          <p><span className="font-bold text-zinc-50">L Funds (Lifecycle)</span> — Auto-rebalancing funds tied to your retirement year (L 2055, L 2060, etc.). Pick one, set it, forget it.</p>
        </div>
      </div>

      {/* Pros / Cons */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="bg-emerald-900/10 border border-emerald-800/30 rounded-sm p-5">
          <h3 className="text-sm font-heading font-bold text-emerald-300 mb-3">Pros</h3>
          <PointList items={[
            "Lowest fees of any retirement plan — ~0.05% expense ratio.",
            "Government matches up to 5% under FERS/BRS.",
            "Simple menu — no overwhelming choices.",
            "Lifecycle funds make 'set it and forget it' easy.",
            "Loans against your TSP are available (with conditions).",
          ]} />
        </div>
        <div className="bg-red-900/10 border border-red-800/30 rounded-sm p-5">
          <h3 className="text-sm font-heading font-bold text-red-300 mb-3">Cons</h3>
          <PointList tone="bad" items={[
            "Limited to 5 core funds + lifecycle blends (less flexibility).",
            "No individual stocks, ETFs, or crypto inside TSP.",
            "Withdrawal rules used to be rigid (improved with the TSP Modernization Act).",
            "Rolling out to an IRA gives you more flexibility but loses the ultra-low fees.",
            "Roth TSP option only matched into a Traditional bucket (matches are always pre-tax).",
          ]} />
        </div>
      </div>

      {/* Bottom line */}
      <div className="bg-amber-900/10 border border-amber-800/30 rounded-sm p-5">
        <h3 className="text-sm font-heading font-bold text-amber-300 mb-2 flex items-center gap-2">
          <Bank size={14} weight="bold" /> Bottom Line
        </h3>
        <p className="text-xs text-zinc-300 leading-relaxed">
          If you're a federal employee or in the military, max out at least the 5% needed to get the full government match. After that, the TSP's ultra-low fees make it one of the best long-term wealth-building vehicles in America. The C Fund alone has built a lot of federal millionaires.
        </p>
      </div>
    </div>
  );
}
